import React, { useEffect, useState } from 'react';
import { 
  ArrowRight, 
  Users, 
  HeadphonesIcon, 
  Search,
  ClipboardCheck,
  Shield,
  Zap,
  Clock,
  Settings,
  FileText,
  Network,
  CheckCircle,
  BarChart,
  Brain,
  Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Composant ServiceCard amélioré
const ServiceCard = ({ icon: Icon, title, description, features, cta, ctaLink }) => {
  const navigate = useNavigate();
  
  return (
    <div className="group">
      <div className="relative bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-white/10 
                    hover:border-green-500/50 transition-all duration-300">
        {/* Effet de brillance */}
        <div className="absolute -inset-[1px] bg-gradient-to-r from-green-500 to-[#017D7E] rounded-xl opacity-0 
                      group-hover:opacity-20 blur-sm transition-opacity duration-300" />
        
        <div className="relative z-10">
          <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6 transform group-hover:scale-110 
                       transition-transform duration-300">
            <Icon className="w-6 h-6 text-[#017D7E]" />
          </div>
          
          <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-[#017D7E] transition-colors">
            {title}
          </h3>
          
          <p className="text-white/70 mb-6">
            {description}
          </p>
          
          <ul className="space-y-3 mb-8">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-white/70">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                {feature}
              </li>
            ))}
          </ul>
          
          <button
            onClick={() => navigate(ctaLink)}
            className="w-full py-3 rounded-lg border border-green-500/50 text-white font-medium
                     hover:bg-[#017D7E]/10 transition-all relative overflow-hidden group">
            <span className="relative z-10 flex items-center justify-center gap-2">
              {cta}
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent
                          translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant FeatureCard amélioré
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="group">
    <div className="relative bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-white/10 
                  hover:border-green-500/50 transition-all duration-300 h-full">
      {/* Effet de brillance */}
      <div className="absolute -inset-[1px] bg-gradient-to-r from-green-500 to-[#017D7E] rounded-xl opacity-0 
                    group-hover:opacity-20 blur-sm transition-opacity duration-300" />
      
      <div className="relative z-10">
        <div className="w-16 h-16 rounded-xl bg-[#017D7E]/10 flex items-center justify-center mx-auto mb-6
                      transform group-hover:scale-110 group-hover:bg-[#017D7E]/20 transition-all duration-300">
          <Icon className="w-8 h-8 text-[#017D7E]" />
        </div>
        
        <h3 className="text-xl font-bold mb-4 text-center group-hover:text-[#017D7E] transition-colors">
          {title}
        </h3>
        
        <p className="text-white/70 text-center">
          {description}
        </p>
      </div>
    </div>
  </div>
);

// Composant ParticlesBackground
const ParticlesBackground = () => (
  <div className="fixed inset-0 z-0 overflow-hidden">
    {[...Array(50)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-[#017D7E]/20"
        style={{
          width: Math.random() * 4 + 'px',
          height: Math.random() * 4 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%',
          animation: `float ${Math.random() * 6 + 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 4}s`
        }}
      />
    ))}
  </div>
);

// Styles CSS pour les animations
const styles = `
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.5; }
}

@keyframes gradientBG {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.gradient-border {
  background: linear-gradient(60deg, #f79533, #f37055, #ef4e7b, #a166ab, #5073b8, #1098ad, #07b39b, #6fba82);
  background-size: 300% 300%;
  animation: gradientBG 4s ease infinite;
}

.floating {
  animation: float 6s ease-in-out infinite;
}

.pulse {
  animation: pulse 2s ease-in-out infinite;
}

.text-glow {
  text-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
}
`;

// Données des services
const services = [
  {
    icon: Briefcase,
    title: "AMOA & Conseil",
    description: "Accompagnement stratégique et opérationnel dans vos projets de transformation",
    features: [
      "Cadrage et étude de faisabilité",
      "Gestion de projet et PMO",
      "Conduite du changement",
      "Coordination des parties prenantes"
    ],
    cta: "En savoir plus",
    ctaLink: "/services/amoa"
  },
  {
    icon: HeadphonesIcon,
    title: "Support Niveau 1",
    description: "Support utilisateur et assistance technique de premier niveau",
    features: [
      "Assistance utilisateurs 24/7",
      "Résolution des incidents",
      "Suivi et reporting",
      "Base de connaissances"
    ],
    cta: "Découvrir notre support",
    ctaLink: "/services/support"
  },
  {
    icon: Search,
    title: "Audit des SI",
    description: "Évaluation et optimisation de vos systèmes d'information",
    features: [
      "Analyse de l'architecture",
      "Audit de sécurité",
      "Évaluation des performances",
      "Recommandations stratégiques"
    ],
    cta: "Voir nos audits",
    ctaLink: "/services/audit"
  }
];

// Les caractéristiques clés
const features = [
  {
    icon: ClipboardCheck,
    title: "Méthodologie Éprouvée",
    description: "Approche structurée et certifiée pour tous vos projets"
  },
  {
    icon: Shield,
    title: "Expertise Sectorielle",
    description: "Connaissance approfondie du secteur financier"
  },
  {
    icon: Users,
    title: "Équipes Dédiées",
    description: "Consultants expérimentés et spécialisés"
  },
  {
    icon: Clock,
    title: "Engagement Garanti",
    description: "SLA et engagements de résultats"
  }
];

// Composant principal
const ServicesLanding = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});

  // Injection des styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  // Hook pour l'effet Parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      const parallaxElements = document.querySelectorAll('.parallax');
      parallaxElements.forEach(element => {
        const speed = element.getAttribute('data-speed') || 0.2;
        const x = (window.innerWidth - e.pageX * speed) / 100;
        const y = (window.innerHeight - e.pageY * speed) / 100;
        element.style.transform = `translateX(${x}px) translateY(${y}px)`;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Observer pour les animations au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach(
      el => observer.observe(el)
    );

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen text-white bg-[#050A1C] relative overflow-hidden">
      <ParticlesBackground />

      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
      </div>

      <div className="fixed inset-0 z-0 opacity-30">
        <div className="absolute inset-0" 
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '4rem 4rem',
            maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
          }} />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-[90vh] flex items-center justify-center relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-[#017D7E]/20 text-[#017D7E] mb-8 
                            relative group hover:scale-105 transition-transform duration-300 cursor-default">
                <div className="relative z-10 flex items-center gap-2">
                  Services Professionnels
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-[#017D7E]/10 rounded-full animate-pulse" />
              </div>

              <h1 className="text-4xl md:text-7xl font-bold mb-8 leading-tight animate-on-scroll"
                  id="hero-title"
                  style={{
                    opacity: isVisible['hero-title'] ? 1 : 0,
                    transform: isVisible['hero-title'] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: 'all 1000ms'
                  }}>
                Expertise et
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]
                              relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px]
                              after:bg-gradient-to-r after:from-[#017D7E] after:to-[#00A99D]">
                  Accompagnement
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/70 mb-12 parallax" data-speed="0.1">
                Des services d'excellence pour accompagner votre transformation digitale
                et garantir la performance de vos systèmes d'information.
              </p>

              <button
                onClick={() => navigate('/contact')}
                className="group relative px-6 md:px-8 py-3 md:py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E]
                          hover:shadow-lg hover:shadow-green-500/20 text-white font-medium transition-all duration-300
                          hover:scale-105">
                <span className="relative z-10 flex items-center gap-2">
                  Échanger avec nos Experts
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg opacity-0
                              transition-opacity duration-300 group-hover:opacity-100" />
              </button>
            </div>
          </div>
        </section>

        {/* Section Services */}
        <section className="py-24 relative">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16 animate-on-scroll" id="services-title">
                <h2 className="text-3xl md:text-4xl font-bold mb-6"
                    style={{
                      opacity: isVisible['services-title'] ? 1 : 0,
                      transition: 'opacity 1000ms'
                    }}>
                  Nos Services
                </h2>
                <p className="text-lg md:text-xl text-white/70">
                  Des solutions adaptées à vos besoins spécifiques
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {services.map((service, index) => (
                  <div key={index}
                       className="animate-on-scroll"
                       id={`service-${index}`}
                       style={{
                         opacity: isVisible[`service-${index}`] ? 1 : 0,
                         transform: isVisible[`service-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                         transition: `all 1000ms ${index * 200}ms`
                       }}>
                    <ServiceCard {...service} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section Features */}
        <section className="py-24 relative">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent opacity-20"
                 style={{ transform: 'skewY(-12deg)' }} />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Notre
                  <span className="relative text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                    {" "}Expertise
                  </span>
                </h2>
                <p className="text-lg md:text-xl text-white/70">
                  Une équipe expérimentée à votre service
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <div key={index}
                       className="animate-on-scroll"
                       id={`feature-${index}`}
                       style={{
                         opacity: isVisible[`feature-${index}`] ? 1 : 0,
                         transform: isVisible[`feature-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                         transition: `all 1000ms ${index * 200}ms`
                       }}>
                    <FeatureCard {...feature} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-t from-green-500/10 to-transparent opacity-20" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-on-scroll"
                  id="cta-title"
                  style={{
                    opacity: isVisible['cta-title'] ? 1 : 0,
                    transform: isVisible['cta-title'] ? 'translateY(0)' : 'translateY(2rem)',
                  }}>
                Prêt à Transformer Votre Business ?
              </h2>
              <p className="text-lg md:text-xl text-white/70 mb-12">
                Nos experts sont à votre disposition pour étudier vos besoins
                et vous proposer un accompagnement sur mesure.
              </p>

              <div className="flex flex-col md:flex-row gap-6 justify-center">
                <button
                  onClick={() => navigate('/contact')}
                  className="group relative px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E]
                            hover:shadow-lg hover:shadow-green-500/20 text-white font-medium transition-all duration-300
                            hover:scale-105">
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Nous contacter
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg opacity-0
                              transition-opacity duration-300 group-hover:opacity-100" />
                </button>

                <button
                  onClick={() => navigate('/about')}
                  className="px-8 py-4 rounded-lg border border-green-500/50 hover:bg-[#017D7E]/10 text-white
                           font-medium transition-all hover:border-green-400">
                  En savoir plus
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServicesLanding;